/* eslint-disable indent */
"use client";

import React, { useContext, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Clipboard, Eye, Heart, Image as ImageIcon, Info, Pencil, Plus, SparkleIcon, Tag, Users, X } from "lucide-react";
import Image from "next/image";
import LoraCard from "@/components/atoms/LoraCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { updateImage } from "@/lib/actions";
import { AppContext } from "@/lib/contexts";
import { cn } from "@/lib/utils";
import UserCard from "components/atoms/UserCard";
import { ImageDTO, ImageMetadata, IUserDTO, LoraDTO, RequestType } from "types";
interface MetadataSectionProps {
  imageInfo: ImageDTO | null;
}
const MetadataSection: React.FC<MetadataSectionProps> = ({
  imageInfo
}) => {
  const {
    state
  } = useContext(AppContext);
  const [editField, setEditField] = useState<string | null>(null);
  const [isCreatorOpen, setIsCreatorOpen] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [metadata, setMetadata] = useState<ImageMetadata>(imageInfo?.imageMetadata || {
    npcFieldsMap: {},
    theme: "",
    premium: false,
    username: "",
    requestType: RequestType.CHARACTER,
    description: "",
    userDTO: {} as IUserDTO,
    modelData: undefined,
    name: "",
    tags: []
  });
  useEffect(() => {
    if (imageInfo && (metadata.description !== imageInfo.imageMetadata.description || metadata.name !== imageInfo.imageMetadata.name || !arraysEqual(metadata.tags || [], imageInfo.imageMetadata.tags || []))) {
      setMetadata(imageInfo.imageMetadata);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageInfo]);

  // Helper function to compare arrays for equality
  const arraysEqual = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  };
  if (!imageInfo) return null;
  const formatRequestType = (requestType: string) => {
    if (!requestType) return "";
    return requestType.charAt(0).toUpperCase() + requestType.slice(1).toLowerCase();
  };
  const handleChange = (field: keyof ImageMetadata, value: string | boolean | string[]) => {
    setMetadata(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSave = async () => {
    if (!metadata || Object.keys(metadata).length === 0) return;
    await updateImage(imageInfo.id, metadata);
    imageInfo.imageMetadata = metadata;
    setEditField(null);
  };
  const handleCancel = (field: keyof ImageMetadata) => {
    setMetadata(prev => ({
      ...prev,
      [field]: imageInfo.imageMetadata[field]
    }));
    setEditField(null);
  };
  const handleAddTag = () => {
    if (!newTag.trim()) return;
    const currentTags = metadata.tags || [];
    if (!currentTags.includes(newTag.trim())) {
      handleChange("tags", [...currentTags, newTag.trim()]);
    }
    setNewTag("");
  };
  const handleRemoveTag = (tagToRemove: string) => {
    const currentTags = metadata.tags || [];
    handleChange("tags", currentTags.filter(tag => tag !== tagToRemove));
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };
  const userImage = state.username === imageInfo.imageMetadata.userDTO.username;

  // Get a type-specific badge color
  const getTypeBadgeClass = (requestType: string) => {
    const types: Record<string, string> = {
      NPC: "!bg-indigo-700 hover:!bg-indigo-800",
      CHARACTER: "!bg-emerald-700 hover:!bg-emerald-800",
      MONSTER: "!bg-red-700 hover:!bg-red-800",
      ITEM: "!bg-amber-700 hover:!bg-amber-800",
      LANDSCAPE: "!bg-teal-700 hover:!bg-teal-800",
      BATTLEMAP: "!bg-blue-700 hover:!bg-blue-800",
      EDIT: "!bg-purple-700 hover:!bg-purple-800",
      VIDEO: "!bg-rose-700 hover:!bg-rose-800",
      AUDIO: "!bg-cyan-700 hover:!bg-cyan-800"
    };
    return types[requestType] || "!bg-slate-700 hover:!bg-slate-800";
  };

  // Filter out empty fields from npcFieldsMap
  const filteredNpcFields = imageInfo.imageMetadata.npcFieldsMap ? Object.entries(imageInfo.imageMetadata.npcFieldsMap).filter(([, value]) => value && value !== "None" && value !== "") : [];

  // Determine if we have model data to display
  const hasModelData = imageInfo.imageMetadata.modelData && imageInfo.imageMetadata.modelData.model && imageInfo.imageMetadata.modelData.model !== "None";

  // Define user stats for compact display
  const userStats = {
    followers: imageInfo.imageMetadata.userDTO.stats?.["Followers"] || 0,
    likes: imageInfo.imageMetadata.userDTO.stats?.["Total Likes"] || 0,
    images: imageInfo.imageMetadata.userDTO.stats?.["Images Generated"] || 0
  };
  return <Card className="!bg-[#111827] !border !border-[#1a2234] !shadow-lg" data-sentry-element="Card" data-sentry-component="MetadataSection" data-sentry-source-file="index.tsx">
            <CardHeader className="!pb-2 !border-b !border-[#1e293b] bg-gradient-to-r from-[#0f172a] to-[#1e293b]" data-sentry-element="CardHeader" data-sentry-source-file="index.tsx">
                <CardTitle className="!text-base !font-bold text-sky-700 !flex !items-center" data-sentry-element="CardTitle" data-sentry-source-file="index.tsx">
                    <Info className="h-4 w-4 mr-2 text-sky-400" data-sentry-element="Info" data-sentry-source-file="index.tsx" />
                    Image Details
                </CardTitle>
            </CardHeader>

            <CardContent className="!p-3 !space-y-3" data-sentry-element="CardContent" data-sentry-source-file="index.tsx">
                {/* Creator Collapsible Section */}
                <Collapsible open={isCreatorOpen} onOpenChange={setIsCreatorOpen} className="!bg-[#1a2234]/60 !rounded-lg !border !border-[#1e293b]/50 !overflow-hidden transition-all duration-200" data-sentry-element="Collapsible" data-sentry-source-file="index.tsx">
                    <CollapsibleTrigger className="flex !items-center !justify-between !w-full !p-2.5 !text-left hover:!bg-[#1e293b]/40 transition-colors" data-sentry-element="CollapsibleTrigger" data-sentry-source-file="index.tsx">
                        <div className="flex items-center gap-2">
                            <div>
                                <p className="text-sm font-semibold text-gray-200">
                                    {imageInfo.imageMetadata.userDTO.username}
                                </p>
                                <p className="text-xs text-gray-400" suppressHydrationWarning>
                                    {new Date(imageInfo.createdAt).toLocaleString()}
                                </p>
                            </div>
                        </div>

                        {/* Stats preview */}
                        <div className="hidden md:flex items-center gap-3 mr-6 text-xs">
                            <div className="flex items-center gap-1 text-sky-400">
                                <Users size={14} data-sentry-element="Users" data-sentry-source-file="index.tsx" />
                                <span className="text-gray-300">
                                    {userStats.followers}
                                </span>
                            </div>
                            <div className="flex items-center gap-1 text-rose-400">
                                <Heart size={14} data-sentry-element="Heart" data-sentry-source-file="index.tsx" />
                                <span className="text-gray-300">
                                    {userStats.likes}
                                </span>
                            </div>
                            <div className="flex items-center gap-1 text-indigo-400">
                                <ImageIcon size={14} data-sentry-element="ImageIcon" data-sentry-source-file="index.tsx" />
                                <span className="text-gray-300">
                                    {userStats.images}
                                </span>
                            </div>
                            <div className="flex items-center gap-1 text-emerald-400">
                                <Eye size={14} data-sentry-element="Eye" data-sentry-source-file="index.tsx" />
                                <span className="text-gray-300">
                                    {imageInfo.viewCount || 0}
                                </span>
                            </div>
                        </div>

                        <div className="text-gray-400">
                            {isCreatorOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="!p-2 !pt-0" data-sentry-element="CollapsibleContent" data-sentry-source-file="index.tsx">
                        <div className="mt-2 border-t border-[#1e293b] pt-2">
                            <div className="flex justify-center">
                                <UserCard user={imageInfo.imageMetadata.userDTO} data-sentry-element="UserCard" data-sentry-source-file="index.tsx" />
                            </div>
                        </div>
                    </CollapsibleContent>
                </Collapsible>

                {/* Type Badge */}
                <div className="flex items-start justify-between">
                    <Badge className={cn("!px-3 !py-1 !text-xs !font-medium !rounded-lg", getTypeBadgeClass(imageInfo.imageMetadata.requestType))} data-sentry-element="Badge" data-sentry-source-file="index.tsx">
                        {formatRequestType(imageInfo.imageMetadata.requestType)}
                    </Badge>

                    {imageInfo.imageMetadata.premium && <Badge className="!bg-gradient-to-r from-amber-500 to-yellow-600 hover:!from-amber-600 hover:!to-yellow-700 !ml-2 !px-2 !py-1">
                            <SparkleIcon className="h-3 w-3 mr-1" />
                            Premium
                        </Badge>}
                </div>

                {/* Views Badge - Adding it here for mobile visibility */}
                <div className="bg-[#1a2234]/60 rounded-lg border border-[#1e293b]/50 px-3 py-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-[#38bdf8] flex items-center">
                        <Eye className="h-3.5 w-3.5 mr-1.5" data-sentry-element="Eye" data-sentry-source-file="index.tsx" />
                        Views
                    </span>
                    <Badge variant="secondary" className="bg-sky-700 text-gray-200 font-medium" data-sentry-element="Badge" data-sentry-source-file="index.tsx">
                        {imageInfo.viewCount?.toLocaleString() || 0}
                    </Badge>
                </div>

                {/* Title Section */}
                <div className="bg-[#1a2234]/60 rounded-lg border border-[#1e293b]/50 p-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center text-xs text-[#38bdf8] font-medium">
                            <Tag className="h-3 w-3 mr-1.5" data-sentry-element="Tag" data-sentry-source-file="index.tsx" />
                            Title
                        </div>
                        {!editField && userImage && <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button size="sm" variant="ghost" className="!h-6 !w-6 !p-0 !text-gray-400 hover:!text-white hover:!bg-[#1e293b]/40" onClick={() => setEditField("name")}>
                                            <Pencil className="h-3 w-3" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="left">
                                        <p>Edit title</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>}
                    </div>

                    {editField === "name" ? <div className="space-y-2 mt-1.5">
                            <Input className="!bg-[#0f172a] !border-[#1e293b] !text-white !text-xs" value={metadata.name || ""} onChange={e => handleChange("name", e.target.value)} placeholder="Enter title..." />
                            <div className="flex gap-2">
                                <Button size="sm" onClick={handleSave} className="!h-6 !text-xs !bg-sky-600 hover:!bg-sky-700 !text-white">
                                    Save
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleCancel("name")} className="!h-6 !text-xs !border-[#1e293b] !text-gray-300 hover:!bg-[#1e293b] hover:!text-white">
                                    Cancel
                                </Button>
                            </div>
                        </div> : metadata.name || userImage ? <p className="text-sm font-medium leading-relaxed text-gray-200 mt-1.5">
                            {metadata.name || "Enter title here"}
                        </p> : null}
                </div>

                {/* Description Section */}
                {imageInfo.imageMetadata.description && <div className="bg-[#1a2234]/60 rounded-lg border border-[#1e293b]/50 p-3">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center text-xs text-[#38bdf8] font-medium">
                                <Info className="h-3 w-3 mr-1.5" />
                                Description
                            </div>
                            {!editField && userImage && <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button size="sm" variant="ghost" className="!h-6 !w-6 !p-0 !text-gray-400 hover:!text-white hover:!bg-[#1e293b]/40" onClick={() => setEditField("description")}>
                                                <Pencil className="h-3 w-3" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="left">
                                            <p>Edit description</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>}
                        </div>

                        {editField === "description" ? <div className="space-y-2 mt-1.5">
                                <Textarea className="!min-h-[60px] !bg-[#0f172a] !border-[#1e293b] !text-white !text-xs" value={metadata.description || ""} onChange={e => handleChange("description", e.target.value)} placeholder="Enter description..." />
                                <div className="flex gap-2">
                                    <Button size="sm" onClick={handleSave} className="!h-6 !text-xs !bg-sky-600 hover:!bg-sky-700 !text-white">
                                        Save
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => handleCancel("description")} className="!h-6 !text-xs !border-[#1e293b] !text-gray-300 hover:!bg-[#1e293b] hover:!text-white">
                                        Cancel
                                    </Button>
                                </div>
                            </div> : <p className="text-sm leading-relaxed text-gray-200 mt-1.5">
                                {metadata.description}
                            </p>}
                    </div>}

                {/* Prompt */}
                {imageInfo.imageMetadata.theme && <div className="bg-[#1a2234]/60 rounded-lg border border-[#1e293b]/50 p-3">
                        <div className="flex items-center text-xs text-[#38bdf8] font-medium mb-2">
                            <Clipboard className="h-3 w-3 mr-1.5" />
                            Prompt
                        </div>
                        <div className="p-2.5 bg-[#0f172a] rounded border border-[#1e293b] text-sm text-gray-200">
                            {imageInfo.imageMetadata.theme}
                        </div>
                    </div>}

                {/* Accordion Sections */}
                <Accordion type="multiple" className="!w-full !space-y-2 !mt-2" defaultValue={[]} data-sentry-element="Accordion" data-sentry-source-file="index.tsx">
                    {/* Additional Fields Section */}
                    {filteredNpcFields.length > 0 && <AccordionItem value="additional-fields" className="border-none">
                            <AccordionTrigger className="!bg-[#1a2234]/60 !px-3 !py-2 !rounded-lg hover:!no-underline 
                                         hover:!bg-[#1e293b]/40 !text-[#38bdf8] !border !border-[#1e293b]/50">
                                <div className="flex items-center text-xs font-medium">
                                    <Tag className="h-3 w-3 mr-1.5" />
                                    Additional Fields
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className="!pt-2 !px-0 !text-gray-200">
                                <div className="bg-[#1a2234]/40 p-3 rounded-lg border border-[#1e293b]/30">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {filteredNpcFields.map(([key, value]) => <div key={key} className={`space-y-1 ${key === "ControlNet Image" ? "col-span-full" : ""}`}>
                                                    <span className="text-xs font-medium text-[#38bdf8] block">
                                                        {key}
                                                    </span>
                                                    {key === "ControlNet Image" ? <div className="relative aspect-square w-full max-w-[150px] overflow-hidden rounded-md border border-[#1e293b]">
                                                            <Image src={value} alt="ControlNet Image" layout="fill" objectFit="cover" className="transition-transform hover:scale-105" />
                                                        </div> : <p className="text-xs p-2 bg-[#0f172a] rounded border border-[#1e293b]">
                                                            {value}
                                                        </p>}
                                                </div>)}
                                    </div>
                                </div>
                            </AccordionContent>
                        </AccordionItem>}

                    {/* Model Information */}
                    {hasModelData && <AccordionItem value="model-info" className="border-none">
                            <AccordionTrigger className="!bg-[#1a2234]/60 !px-3 !py-2 !rounded-lg hover:!no-underline 
                                         hover:!bg-[#1e293b]/40 !text-[#38bdf8] !border !border-[#1e293b]/50">
                                <div className="flex items-center text-xs font-medium">
                                    <SparkleIcon className="h-3 w-3 mr-1.5" />
                                    Model Information
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className="!pt-2 !px-0">
                                <div className="bg-[#1a2234]/40 p-3 rounded-lg border border-[#1e293b]/30">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {imageInfo.imageMetadata.modelData?.model && <div className="space-y-1">
                                                <span className="text-xs font-medium text-[#38bdf8]">
                                                    Model
                                                </span>
                                                <p className="text-xs p-2 bg-[#0f172a] rounded border border-[#1e293b] text-gray-200">
                                                    {imageInfo.imageMetadata.modelData.model}
                                                </p>
                                            </div>}

                                        {"steps" in (imageInfo.imageMetadata.modelData || {}) && imageInfo.imageMetadata.modelData?.steps && <div className="space-y-1">
                                                    <span className="text-xs font-medium text-[#38bdf8]">
                                                        Steps
                                                    </span>
                                                    <p className="text-xs p-2 bg-[#0f172a] rounded border border-[#1e293b] text-gray-200">
                                                        {imageInfo.imageMetadata.modelData.steps}
                                                    </p>
                                                </div>}

                                        {"cfg" in (imageInfo.imageMetadata.modelData || {}) && imageInfo.imageMetadata.modelData?.cfg && <div className="space-y-1">
                                                    <span className="text-xs font-medium text-[#38bdf8]">
                                                        CFG Scale
                                                    </span>
                                                    <p className="text-xs p-2 bg-[#0f172a] rounded border border-[#1e293b] text-gray-200">
                                                        {imageInfo.imageMetadata.modelData.cfg}
                                                    </p>
                                                </div>}

                                        {"sampler" in (imageInfo.imageMetadata.modelData || {}) && imageInfo.imageMetadata.modelData?.sampler && <div className="space-y-1">
                                                    <span className="text-xs font-medium text-[#38bdf8]">
                                                        Sampler
                                                    </span>
                                                    <p className="text-xs p-2 bg-[#0f172a] rounded border border-[#1e293b] text-gray-200">
                                                        {imageInfo.imageMetadata.modelData.sampler}
                                                    </p>
                                                </div>}
                                    </div>
                                </div>
                            </AccordionContent>
                        </AccordionItem>}

                    {/* Lora Section */}
                    {imageInfo.loraList && imageInfo.loraList.length > 0 && <AccordionItem value="loras" className="border-none">
                            <AccordionTrigger className="!bg-[#1a2234]/60 !px-3 !py-2 !rounded-lg hover:!no-underline 
                                         hover:!bg-[#1e293b]/40 !text-[#38bdf8] !border !border-[#1e293b]/50">
                                <div className="flex items-center text-xs font-medium">
                                    <SparkleIcon className="h-3 w-3 mr-1.5" />
                                    Loras
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className="pt-2 px-0">
                                <div className="bg-[#1a2234]/40 p-3 rounded-lg border border-[#1e293b]/30">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {imageInfo.loraList.map((lora: LoraDTO, index: number) => <LoraCard key={index} lora={lora} />)}
                                    </div>
                                </div>
                            </AccordionContent>
                        </AccordionItem>}
                </Accordion>
                {/* Tags Section */}
                <div className="bg-[#1a2234]/60 rounded-lg border border-[#1e293b]/50 p-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center text-xs text-[#38bdf8] font-medium">
                            <Tag className="h-3 w-3 mr-1.5" data-sentry-element="Tag" data-sentry-source-file="index.tsx" />
                            Tags
                        </div>
                        {!editField && userImage && <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button size="sm" variant="ghost" className="!h-6 !w-6 !p-0 !text-gray-400 hover:!text-white hover:!bg-[#1e293b]/40" onClick={() => setEditField("tags")}>
                                            <Pencil className="h-3 w-3" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="left">
                                        <p>Edit tags</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>}
                    </div>

                    {editField === "tags" ? <div className="space-y-3 mt-1.5">
                            <div className="flex flex-wrap gap-1.5">
                                {(metadata.tags || []).map(tag => <Badge key={tag} className="!bg-slate-700 hover:!bg-slate-600 !text-white !flex !items-center !gap-1 !px-2 !py-1">
                                        {tag}
                                        <Button size="sm" variant="ghost" className="!h-3.5 !w-3.5 !p-0 !ml-1 !rounded-full !bg-slate-600/40 hover:!bg-slate-500/60" onClick={() => handleRemoveTag(tag)}>
                                            <X className="h-2 w-2" />
                                        </Button>
                                    </Badge>)}
                            </div>

                            <div className="flex gap-2">
                                <Input className="!bg-[#0f172a] !border-[#1e293b] !text-white !text-xs" value={newTag} onChange={e => setNewTag(e.target.value)} onKeyDown={handleKeyDown} placeholder="Add new tag..." />
                                <Button size="sm" onClick={handleAddTag} className="!h-8 !text-xs !bg-slate-700 hover:!bg-slate-600 !text-white">
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add
                                </Button>
                            </div>

                            <div className="flex gap-2">
                                <Button size="sm" onClick={handleSave} className="!h-6 !text-xs !bg-sky-600 hover:!bg-sky-700 !text-white">
                                    Save
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleCancel("tags")} className="!h-6 !text-xs !border-[#1e293b] !text-gray-300 hover:!bg-[#1e293b] hover:!text-white">
                                    Cancel
                                </Button>
                            </div>
                        </div> : <div className="flex flex-wrap gap-1.5">
                            {(metadata.tags || []).length > 0 ? (metadata.tags || []).map(tag => <Badge key={tag} className="bg-slate-700 hover:bg-slate-600 text-white !px-2 !py-1">
                                        {tag}
                                    </Badge>) : <p className="text-xs text-gray-400">
                                    No tags added yet
                                </p>}
                        </div>}
                </div>
            </CardContent>
        </Card>;
};
export default MetadataSection;